import { useEffect, Component } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import AudioPlayer from "@/components/AudioPlayer";
import AuthCallback from "@/components/AuthCallback";
import Intro from "@/pages/Intro";
import Portfolio from "@/pages/Portfolio";
import Services from "@/pages/Services";
import Admin from "@/pages/Admin";
import Auth from "@/pages/Auth";
import Account from "@/pages/Account";
import Receipt from "@/pages/Receipt";
import Commission from "@/pages/Commission";
import { SiteContentProvider } from "@/components/contentContext";
import { useLenis } from "@/hooks/useLenis";
import "@/App.css";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-ink flex items-center justify-center px-6 text-center">
          <p className="font-display text-xl tracking-[0.2em] text-slate-400">
            The night veiled this page. Refresh to return.
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const Shell = () => {
  useLenis();
  const location = useLocation();
  const { pathname } = location;

  useEffect(() => {
    const track = () => {
      if (pathname.startsWith("/admin") || pathname.startsWith("/receipt/")) return;
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        body: JSON.stringify({
          path: pathname,
          referrer: document.referrer,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          language: navigator.language,
        }),
      }).catch(() => {});
    };
    track();
  }, [pathname]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <div className="grain relative min-h-screen bg-ink pb-24">
      <ScrollToTop />
      <Nav />
      <Routes>
        <Route path="/" element={<Intro />} />
        <Route path="/portfolio" element={<Portfolio />} />
        <Route path="/services" element={<Services />} />
        <Route path="/commission" element={<Commission />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/account" element={<Account />} />
        <Route path="/receipt/:id" element={<Receipt />} />
        <Route path="/admin" element={<Admin />} />
      </Routes>
      <Footer />
      {pathname !== "/admin" && <AudioPlayer />}
    </div>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <SiteContentProvider>
        <BrowserRouter>
          <Shell />
          <Toaster position="bottom-right" />
        </BrowserRouter>
      </SiteContentProvider>
    </ErrorBoundary>
  );
}

export default App;
